import React, { useCallback, useEffect, useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Box, Icon, Modal, Text } from '@nimbus-ds/components';
import { CalculatorIcon } from '@nimbus-ds/icons';
import { goTo } from '@tiendanube/nexo';
import { Country } from 'App/i18n/enums';
import nexo from 'App/nexoClient';
import { ADMIN_ROUTES } from 'App/Routes/constants';
import { useStoreInfo } from 'commons/hooks';
import {
  PaymentMethodsType,
  PaymentProvidersComparisonParams,
} from 'commons/types';
import { Currency } from 'commons/types/Currency.enum';
import { useTranslationWithPrefix } from 'commons/utils';
import { CountryToCurrency } from 'commons/utils/country-currency';
import { useAvailableInstallments } from '../../hooks/useAvailableInstallments';
import { useFeeCalculatorResults } from '../../hooks/useFeeCalculatorResults';
import useGetPlans from '../../hooks/useGetPlans';
import CalculatedResultsContainer from './CalculatedResults/CalculatedResultsContainer';
import FeeCalculatorBody from './FeeCalculatorBody';

const initialCalculatorData: PaymentProvidersComparisonParams = {
  amount: 1000,
  currency: Currency.MXN,
  tiendanubePlan: 'mex-basic-plan',
  paymentMethodType: PaymentMethodsType.CreditCard,
  installments: 1,
  releaseDays: 0,
};

const trackCalculatorMap: Record<string, string> = {
  amount: 'nuvempago_mexico_landing_page_calculator_input_value_insert',
  tiendanubePlan:
    'nuvempago_mexico_landing_page_calculator_account_level_select',
  paymentMethodType:
    'nuvempago_mexico_landing_page_calculator_payment_method_select',
  installments: 'nuvempago_mexico_landing_page_calculator_installments_select',
};

interface FeeCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeeCalculatorModal: React.FC<FeeCalculatorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslationWithPrefix('landing.feeCalculatorModal');

  const [calculatorData, setCalculatorData] =
    useState<PaymentProvidersComparisonParams>(initialCalculatorData);

  const { storeInfo } = useStoreInfo();

  const {
    results,
    isLoading,
    errorKey,
    fetchFeeCalculatorResults,
    resetFeeCalculatorResults,
  } = useFeeCalculatorResults();

  const {
    data: plans,
    isLoading: isLoadingPlans,
    error: errorPlans,
  } = useGetPlans({
    regionalPaymentGatewayId: Country.MEXICO,
    custom: false,
    active: true,
  });

  const availableInstallments = useAvailableInstallments(
    plans,
    calculatorData.tiendanubePlan,
  );

  useEffect(() => {
    if (isOpen) {
      track('nuvempago_mexico_landing_page_calculator_input_value_insert');
    }
  }, [isOpen]);

  useEffect(() => {
    if (errorKey) {
      setCalculatorData(initialCalculatorData);
    }
  }, [errorKey]);

  useEffect(() => {
    const isMsi = calculatorData.paymentMethodType === PaymentMethodsType.Msi;
    if (
      isMsi &&
      availableInstallments.length > 0 &&
      !availableInstallments.includes(calculatorData.installments ?? 0)
    ) {
      setCalculatorData((prev) => ({
        ...prev,
        installments: availableInstallments[0],
      }));
    }
  }, [
    availableInstallments,
    calculatorData.paymentMethodType,
    calculatorData.installments,
  ]);

  const handleChangeCalculatorData = useCallback(
    (key: keyof PaymentProvidersComparisonParams, value: string | number) => {
      setCalculatorData((prevState) => ({
        ...prevState,
        [key]: value,
      }));
      const trackingEvent = trackCalculatorMap[key];
      if (trackingEvent) {
        track(trackingEvent, { value });
      }
    },
    [],
  );

  const handleSubmit = () => {
    const params: PaymentProvidersComparisonParams = {
      amount: calculatorData.amount,
      currency: CountryToCurrency(storeInfo?.country as Country),
      tiendanubePlan: calculatorData.tiendanubePlan,
      paymentMethodType: calculatorData.paymentMethodType,
      releaseDays: calculatorData.releaseDays,
    };

    if (calculatorData.paymentMethodType === PaymentMethodsType.Msi) {
      params.paymentMethodType = PaymentMethodsType.CreditCard;
      params.installments = calculatorData.installments;
    }

    fetchFeeCalculatorResults(params);
    track('tiendanube_mx_pagonube_landing_calculator_calculate_click', {
      calculated_amount: calculatorData.amount,
      account_level: calculatorData.tiendanubePlan,
      payment_method: calculatorData.paymentMethodType,
      installment_count: calculatorData.installments,
    });
  };

  const handleCompareAgain = () => {
    setCalculatorData(initialCalculatorData);
    resetFeeCalculatorResults();
    track('tiendanube_mx_pagonube_landing_calculator_reset_click', {
      previous_calculated_amount: calculatorData.amount,
    });
  };

  const handleActivatePagoNube = () => {
    handleClose();
    track('tiendanube_mx_pagonube_landing_calculator_activate_click');
    goTo(nexo, ADMIN_ROUTES.NUVEMPAGO_PAYMENT_METHODS);
  };

  const handleClose = () => {
    setCalculatorData(initialCalculatorData);
    resetFeeCalculatorResults();
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onDismiss={handleClose}
      maxWidth={{ xs: '90%', md: '450px' }}
    >
      <Modal.Header>
        <Box display="flex" alignItems="center" gap="2" marginBottom="2">
          <Icon source={<CalculatorIcon />} />
          <Text fontWeight="bold" fontSize="highlight" color="neutral-textHigh">
            {t('title')}
          </Text>
        </Box>
        <Text fontSize="caption" color="neutral-textLow">
          {t('description')}
        </Text>
      </Modal.Header>
      <Modal.Body padding="none">
        {!results || results.length === 0 ? (
          <FeeCalculatorBody
            feeCalculatorData={calculatorData}
            handleChangeCalculatorData={handleChangeCalculatorData}
            handleCalculate={handleSubmit}
            isLoading={isLoading}
            errorKey={errorKey}
            plans={plans}
            isLoadingPlans={isLoadingPlans}
            errorPlans={errorPlans}
            availableInstallments={availableInstallments}
          />
        ) : (
          <CalculatedResultsContainer
            results={results}
            paymentMethodType={calculatorData.paymentMethodType}
            onCompareAgain={handleCompareAgain}
            onActivatePagoNube={handleActivatePagoNube}
            installmentsQty={calculatorData.installments ?? 0}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};
